/**
 * Calmont Kaufbereich <cal-buy>
 * - Kaufoption + Variante (Chips) wählen; alle Instanzen (Kaufbox, Popups) bleiben synchron.
 * - Preise, Optionstext, Packshot der Variante, Formular-ID, Versandverlauf aktualisieren.
 * - Popups als <dialog> (Top-Layer): „Option wählen“ und „Welche Seite passt?“.
 * - Mobile Leiste unten: ausgeblendet, solange der Warenkorb-Button der Kaufbox sichtbar ist.
 * - In den Warenkorb: /cart/add.js + Horizon CartLinesUpdateEvent + Drawer öffnen (Muster KOSI/glør).
 * - Abschluss-Section: [data-cal-open-options] öffnet das Options-Popup, falls die Kaufbox da ist.
 */
const money = (cents, fmt) => {
  const v = (cents / 100).toFixed(2);
  const [i, d] = v.split('.');
  const de = i.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + d;
  const f = fmt || '€{{amount_with_comma_separator}}';
  return f.replace(/{{\s*amount_with_comma_separator\s*}}/, de).replace(/{{\s*amount_no_decimals_with_comma_separator\s*}}/, i.replace(/\B(?=(\d{3})+(?!\d))/g, '.'))
    .replace(/{{\s*amount\s*}}/, v).replace(/{{\s*amount_no_decimals\s*}}/, i);
};

class CalBuy extends HTMLElement {
  connectedCallback() {
    try { this.data = JSON.parse(this.querySelector('[data-cal-data]').textContent); } catch (e) { this.data = { options: [] }; }
    // Set-Einträge am Produkt (custom.set_auswahl): eigener Modus
    const sj = this.querySelector('[data-cal-sets]');
    if (sj) {
      try { this.sets = JSON.parse(sj.textContent); } catch (e) { this.sets = null; }
      if (this.sets) { this.initSets(); this.common(); return; }
    }
    const opts = this.data.options;
    if (!opts.length) return;
    const def = opts.find((o) => o.default) || opts[opts.length - 1];
    this.state = { opt: def.id, vars: Object.fromEntries(opts.map((o) => [o.id, (o.variants.find((v) => v.available) || o.variants[0]).id])) };
    this.common();
  }

  common() {
    this.addEventListener('click', (e) => this.onClick(e));
    this.initSwipe();
    const form = this.querySelector('.cal-buy__form');
    form?.addEventListener('submit', (e) => { e.preventDefault(); this.add(form.querySelector('[data-cal-add]')); });
    this.querySelectorAll('.cal-seg').forEach((s) => s.style.setProperty('--seg-n', s.querySelectorAll('.cal-seg__b').length));
    this.querySelectorAll('.cal-tabs').forEach((t) => t.style.setProperty('--tab-n', t.querySelectorAll('.cal-tab').length));
    this.querySelectorAll('[data-cal-close]').forEach((b) => b.addEventListener('click', () => b.closest('dialog')?.close()));
    this.querySelectorAll('dialog').forEach((d) => d.addEventListener('click', (e) => { if (e.target === d) d.close(); }));
    document.addEventListener('click', (e) => {
      const t = e.target.closest('[data-cal-open-options]');
      if (t && !this.contains(t)) { e.preventDefault(); this.open('opts'); }
    });
    this.render();
    this.eta();
    this.watchAtc();
  }

  /* Galerie: Wischen auf dem Hauptbild wechselt zum nächsten/vorherigen Bild */
  initSwipe() {
    const main = this.querySelector('.cal-gal__main');
    // Thumbnails: Antippen auch dann erkennen, wenn der Finger minimal verrutscht (iOS bricht den Klick sonst ab)
    const bar = this.querySelector('.cal-gal__thumbs');
    if (bar) {
      let px = 0, py = 0;
      bar.addEventListener('pointerdown', (e) => { px = e.clientX; py = e.clientY; }, { passive: true });
      bar.addEventListener('pointerup', (e) => {
        if (e.pointerType === 'mouse') return;
        const t = e.target.closest('[data-cal-thumb]');
        if (t && Math.abs(e.clientX - px) < 12 && Math.abs(e.clientY - py) < 12) this.selThumb(t);
      }, { passive: true });
    }
    if (!main) return;
    let x0 = null, y0 = 0, t0 = 0;
    main.addEventListener('touchstart', (e) => { const t = e.touches[0]; x0 = t.clientX; y0 = t.clientY; t0 = Date.now(); }, { passive: true });
    main.addEventListener('touchend', (e) => {
      if (x0 === null) return;
      const t = e.changedTouches[0]; const dx = t.clientX - x0; const dy = t.clientY - y0; x0 = null;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.2 || Date.now() - t0 > 900) return;
      this.galStep(dx < 0 ? 1 : -1);
    }, { passive: true });
  }

  selThumb(t) {
    if (t.getAttribute('aria-current') === 'true') return;
    const img = this.querySelector('.cal-gal__img');
    if (img) { img.srcset = ''; img.src = t.dataset.calThumb; img.alt = t.dataset.alt || ''; }
    this.querySelectorAll('[data-cal-thumb]').forEach((x) => x.setAttribute('aria-current', x === t ? 'true' : 'false'));
    this._altShow = 'set';
    this.galAltSync();
  }

  /* Set gewählt: das Set-Bild ersetzt das erste Galerie-Bild (Hauptbild + erstes Thumbnail).
     Steht das erste Bild vorne, zeigt die Ecke klein das Produktbild zum Umschalten; bei anderen Thumbnails keine Ecke. */
  galSet(url) {
    // serverseitig gesetztes Set-Bild (sections/cal-pdp-hero) übernehmen
    if (this._setImg === undefined) {
      const gm = this.querySelector('.cal-gal__main');
      if (gm && gm.dataset.setImg) { this._setImg = gm.dataset.setImg; this._base = { main: gm.dataset.baseMain, thumb: gm.dataset.baseThumb }; this._altShow = 'set'; }
    }
    if ((url || null) === (this._setImg || null)) return;
    const t0 = this.querySelector('[data-cal-thumb]');
    const img = this.querySelector('.cal-gal__img');
    if (!img) return;
    if (this._base === undefined) this._base = { main: t0 ? t0.dataset.calThumb : (img.currentSrc || img.src), thumb: t0 && t0.querySelector('img') ? t0.querySelector('img').src : '' };
    this._setImg = url || null; this._altShow = 'set';
    const first = !t0 || t0.getAttribute('aria-current') === 'true';
    if (t0) {
      t0.dataset.calThumb = url || this._base.main;
      const ti = t0.querySelector('img'); if (ti) { ti.srcset = ''; ti.src = url ? url.replace(/width=\d+/, 'width=200') : this._base.thumb; }
    }
    if (first) {
      img.classList.remove('is-in-l', 'is-in-r'); void img.offsetWidth;
      img.srcset = ''; img.src = url || this._base.main; if (url) img.classList.add('is-in-r');
    }
    this.galAltSync();
  }

  galAltSync() {
    const main = this.querySelector('.cal-gal__main'); if (!main) return;
    const t0 = this.querySelector('[data-cal-thumb]');
    const first = !t0 || t0.getAttribute('aria-current') === 'true';
    let back = main.querySelector('.cal-gal__alt');
    const show = !!(this._setImg && first);
    if (!show) { if (back) back.hidden = true; return; }
    if (!back) {
      back = document.createElement('button'); back.type = 'button'; back.className = 'cal-gal__alt'; back.innerHTML = '<img alt="">';
      main.append(back);
    }
    if (this._altShow !== 'set' && this._altShow !== 'base') this._altShow = 'set';
    const bi = back.querySelector('img'); bi.removeAttribute('srcset'); bi.removeAttribute('sizes');
    bi.src = this._altShow === 'set' ? this._base.main : this._setImg;
    back.setAttribute('aria-label', this._altShow === 'set' ? 'Produktbild zeigen' : 'Set-Bild zeigen');
    back.hidden = false;
  }

  galStep(dir) {
    const th = [...this.querySelectorAll('[data-cal-thumb]')];
    if (th.length < 2) return;
    let i = th.findIndex((x) => x.getAttribute('aria-current') === 'true'); if (i < 0) i = 0;
    const n = th[(i + dir + th.length) % th.length];
    const img = this.querySelector('.cal-gal__img');
    if (img) {
      img.classList.remove('is-in-l', 'is-in-r'); void img.offsetWidth;
      img.srcset = ''; img.src = n.dataset.calThumb; img.alt = n.dataset.alt || '';
      img.classList.add(dir > 0 ? 'is-in-r' : 'is-in-l');
    }
    th.forEach((x) => x.setAttribute('aria-current', x === n ? 'true' : 'false'));
    n.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    this._altShow = 'set';
    this.galAltSync();
  }


  /* ---------- Messpunkte (window.calTrack, snippets/cal-head.liquid) ---------- */
  trk(n, d = {}) {
    try {
      const h = (location.pathname.split('/products/')[1] || '').split('/')[0];
      if (window.calTrack) window.calTrack(n, Object.assign({ produkt: h }, d));
    } catch (e) {}
  }
  selName() {
    if (!this.sets || !this.st) return 'einzeln';
    if (this.st.sel === 'single') return 'einzeln';
    const g = this.sets.groups.find((x) => x.id === this.st.sel);
    return (g && g.handle) || String(this.st.sel);
  }

  onClick(e) {
    // Eck-Bild in der Galerie: zwischen Set-Bild und Produktbild wechseln
    const alt = e.target.closest('.cal-gal__alt');
    if (alt) {
      e.preventDefault();
      if (this._setImg === undefined) this.galSet(this.querySelector('.cal-gal__main').dataset.setImg || null);
      this._altShow = this._altShow === 'set' ? 'base' : 'set';
      const im = this.querySelector('.cal-gal__img'); im.srcset = ''; im.src = this._altShow === 'set' ? this._setImg : this._base.main;
      this.galAltSync(); return;
    }
    // Info-Link im Text einer Kaufoption (z. B. „Warum ein Wechselbezug?“): Popup öffnen, Option nicht umschalten
    const inl = e.target.closest('[data-cal-sopt] [data-cal-dialog]');
    if (inl) {
      e.preventDefault(); e.stopPropagation();
      // Aus einer Set-Box, die den Bezug schon enthält: nur erklären, keinen weiteren Bezug zum Kauf anbieten
      const dl = this.querySelector(`[data-cal-dlg="${inl.dataset.calDialog}"]`); if (dl) dl.classList.add('is-info');
      this.trk('popup_geoeffnet', { popup: inl.dataset.calDialog }); return this.open(inl.dataset.calDialog);
    }
    if (this.cmppClick(e)) return;
    if (this.sets && this.setsClick(e)) return;
    const t = e.target.closest('[data-cal-opt],[data-cal-var],[data-cal-dialog],[data-cal-lage],[data-cal-thumb],[data-cal-add]');
    if (!t) return;
    if (t.hasAttribute('data-cal-add') && t.type !== 'submit') return this.add(t);
    if (t.dataset.calOpt) { this.state.opt = t.dataset.calOpt; this.trk('kaufbox_option', { wert: (t.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 60) }); return this.render(); }
    if (t.dataset.calVar) { this.state.opt = t.dataset.calOf; this.state.vars[t.dataset.calOf] = +t.dataset.calVar; this.trk('kaufbox_option', { wert: (t.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 60) }); return this.render(); }
    if (t.dataset.calDialog) { e.preventDefault(); const dl = this.querySelector(`[data-cal-dlg="${t.dataset.calDialog}"]`); if (dl) dl.classList.remove('is-info'); this.trk('popup_geoeffnet', { popup: t.dataset.calDialog }); return this.open(t.dataset.calDialog); }
    if (t.dataset.calLage) { const d = t.closest('dialog'); this.trk('popup_tab', { popup: (d && d.dataset.calDlg) || '', tab: t.dataset.calLage }); return this.lage(t); }
    if (t.dataset.calThumb) this.selThumb(t);
  }

  /* ---------- Set-Einträge (custom.set_auswahl) ---------- */
  initSets() {
    const S = this.sets;
    const cur = S.main.variants.find((v) => v.id === S.cur) || S.main.variants[0];
    this.st = { sel: S.sel || 'single', mo: cur ? cur.o.slice() : [], sv: {}, addon: {} };
    S.groups.forEach((g) => { this.st.sv[g.id] = null; });
    S.addons.forEach((a) => { this.st.addon[a.id] = { on: false, vals: (a.names || []).map(() => null) }; });
    // ?set=<Handle des Set-Produkts> (oder ?set=einzeln) wählt die Kaufoption vor, z. B. aus Startseiten-Kacheln
    // Vorauswahl Set, aber für die Startvariante nicht lieferbar: mit „Einzeln“ starten
    const g0 = S.groups.find((g) => g.id === this.st.sel);
    if (g0 && cur && !Object.values(g0.map[cur.id] || {}).some((v) => v.available)) this.st.sel = 'single';
    // ?set=<Handle des Set-Produkts> (oder ?set=einzeln) wählt die Kaufoption vor, z. B. aus Startseiten-Kacheln.
    // Ist das Set für die Startvariante nicht lieferbar, auf die erste Variante wechseln, für die es lieferbar ist.
    const qs = new URLSearchParams(location.search).get('set');
    if (qs === 'einzeln') this.st.sel = 'single';
    const gq = qs && S.groups.find((g) => g.handle === qs);
    if (gq) {
      const ok = (v) => Object.values(gq.map[v.id] || {}).some((x) => x.available);
      const v = cur && ok(cur) ? cur : S.main.variants.find(ok);
      if (v) { this.st.sel = gq.id; this.st.mo = v.o.slice(); }
    }
    // Bevorzugte Werte (Theme-Einstellung „Kaufbox: bevorzugte Werte“, z. B. „Wärmend“)
    const root = this.querySelector('[data-cal-sets-root]');
    this.pref = ((root && root.dataset.pref) || '').split('|').map((x) => x.trim().toLowerCase()).filter(Boolean);
    this.prefHide = !!(root && root.dataset.prefHide === '1');
    // Startwerte Zusatz: bevorzugter Wert der Chips, sonst der erste
    this.querySelectorAll('[data-cal-av][aria-checked="true"]').forEach((b) => { const a = this.st.addon[b.dataset.calAv]; if (a) a.vals[+b.dataset.oi] = b.dataset.val; });
    if (this.pref.length) {
      this.querySelectorAll('[data-cal-av]').forEach((b) => {
        if (this.pref.includes(String(b.dataset.val).trim().toLowerCase())) { const a = this.st.addon[b.dataset.calAv]; if (a) a.vals[+b.dataset.oi] = b.dataset.val; }
      });
    }
  }

  mainVariant() {
    const S = this.sets; const mo = this.st.mo;
    return S.main.variants.find((v) => v.o.every((x, i) => x === mo[i])) || null;
  }

  groupVal(g, mv) {
    // gewählter Set-Wert, sonst erster verfügbarer passender Wert
    const m = (mv && g.map[mv.id]) || {};
    const keys = Object.keys(m);
    let val = this.st.sv[g.id];
    if (val && val.includes('\u0000')) val = null;
    if (!val || !m[val]) {
      // ohne eigene Wahl: lieferbaren Wert mit den meisten bevorzugten Teilen („A | B“), sonst ersten lieferbaren
      const pr = this.pref || [];
      const score = (k) => k.split(' | ').filter((x) => pr.includes(x.trim().toLowerCase())).length;
      const av = keys.filter((k) => m[k].available);
      const best = pr.length ? av.slice().sort((a, b) => score(b) - score(a))[0] : null;
      val = best || av[0] || keys[0] || null;
    }
    return { val, v: val ? m[val] : null, map: m, keys };
  }

  setsClick(e) {
    const mo = e.target.closest('[data-cal-mo]');
    if (mo) {
      const was = this.st.mo[+mo.dataset.calMo];
      this.st.mo[+mo.dataset.calMo] = mo.dataset.val; this._cmpKeys = null; this.render();
      if (was !== mo.dataset.val) this.trk('kaufbox_option', { option: +mo.dataset.calMo + 1, wert: mo.dataset.val, vorher: was || '' });
      return true;
    }
    const so = e.target.closest('[data-cal-sopt]');
    if (so) { const was = this.selName(); this.st.sel = so.dataset.calSopt; this.render(); if (this.selName() !== was) this.trk('kaufbox_auswahl', { auswahl: this.selName(), vorher: was }); return true; }
    const sv = e.target.closest('[data-cal-sv]');
    if (sv) {
      const g = this.sets.groups.find((x) => x.id === sv.dataset.calSv);
      if (sv.dataset.part !== undefined) {
        // Kombi-Werte „A | B“: Teil ersetzen
        const { val } = this.groupVal(g, this.mainVariant());
        const parts = (val || ' | ').split(' | ');
        parts[+sv.dataset.part] = sv.dataset.val;
        this.st.sv[g.id] = parts.join(' | ');
      } else this.st.sv[g.id] = sv.dataset.val;
      const was = this.selName();
      this.st.sel = g.id; this.render();
      if (this.selName() !== was) this.trk('kaufbox_auswahl', { auswahl: this.selName(), vorher: was });
      return true;
    }
    const av = e.target.closest('[data-cal-av]');
    if (av) { const a = this.st.addon[av.dataset.calAv]; a.vals[+av.dataset.oi] = av.dataset.val; a.on = true; this.render(); return true; }
    const ao = e.target.closest('[data-cal-addon-on]');
    if (ao) { this.st.addon[ao.dataset.calAddonOn].on = ao.checked; this.render(); this.trk('kaufbox_zusatz', { zusatz: ao.dataset.calAddonOn, an: ao.checked ? 'ja' : 'nein' }); return true; }
    return false;
  }

  addonPick(a) {
    const st = this.st.addon[a.id];
    const key = (a.names && a.names.length) ? st.vals.join(' | ') : '';
    return a.map[key] || null;
  }

  addonVisible(a) {
    const sel = this.st.sel;
    if (a.onlySingle && sel !== 'single') return false;
    const only = (a.onlySets || []).filter(Boolean);
    if (only.length) { const g = this.sets.groups.find((x) => x.id === sel); return !!(g && only.includes(g.handle)); }
    return true;
  }

  renderSets() {
    const S = this.sets; const fmt = this.dataset.money; const st = this.st;
    const mv = this.mainVariant();
    // Produkt-Optionen: Auswahl + nicht verfügbare Kombinationen
    this.querySelectorAll('[data-cal-mo]').forEach((b) => {
      const i = +b.dataset.calMo; const on = st.mo[i] === b.dataset.val;
      b.setAttribute('aria-checked', on ? 'true' : 'false');
      const test = st.mo.slice(); test[i] = b.dataset.val;
      const v = S.main.variants.find((x) => x.o.every((y, k) => y === test[k]));
      b.classList.toggle('is-out', !v || !v.available);
    });
    this.querySelectorAll('[data-cal-vo]').forEach((w) => { const l = w.querySelector('[data-cal-vo-label]'); if (l) l.textContent = (st.mo[+w.dataset.calVo] || '').replace(/\s*\(.*\)\s*$/, ''); });
    // Karten
    if (st.sel !== 'single' && !S.groups.find((g) => g.id === st.sel)) st.sel = 'single';
    this.querySelectorAll('[data-cal-sopt]').forEach((b) => b.setAttribute('aria-checked', b.dataset.calSopt === st.sel ? 'true' : 'false'));
    const setPrice = (key, v, any) => {
      const out = !v || (any === false);
      this.querySelectorAll(`[data-cal-sopt="${key}"]`).forEach((el) => el.classList.toggle('is-out', out));
      this.querySelectorAll(`[data-cal-sprice="${key}"]`).forEach((el) => { el.textContent = out ? 'Nicht verfügbar' : money(v.price, fmt); });
      this.querySelectorAll(`[data-cal-swas="${key}"]`).forEach((el) => { const show = v && v.compare > v.price; el.hidden = !show || out; if (show) el.textContent = money(v.compare, fmt); });
      this.querySelectorAll(`[data-cal-bwbadge="${key}"]`).forEach((el) => { if (v && v.compare > v.price) el.textContent = el.dataset.pre + Math.round((v.compare - v.price) / v.compare * 100) + el.dataset.post; });
      this.querySelectorAll(`[data-cal-ssave="${key}"]`).forEach((el) => { const show = v && v.compare > v.price && !out; el.hidden = !show; if (show) el.textContent = el.dataset.pctOnly !== undefined ? 'Du sparst ' + Math.round((v.compare - v.price) / v.compare * 100) + ' %' : 'Du sparst ' + money(v.compare - v.price, fmt).replace(/,00(?=\D*$)/, '') + (el.dataset.pct !== undefined ? ' (' + Math.round((v.compare - v.price) / v.compare * 100) + ' %)' : ''); });
    };
    // Set-Bonus (z. B. Black Month: Decke + Kissen 10 % extra): gilt, sobald ein Zusatz mit Bonus gewählt ist
    const bonus = Math.max(0, ...S.addons.map((a) => (a.bonus && this.addonVisible(a) && this.st.addon[a.id].on && this.addonPick(a)?.available ? a.bonus : 0)));
    // bonus = Set-Rabatt in % auf den Aktionspreis, wie Shopify ihn rechnet (Rabatt je Stück auf Cent abgerundet)
    const bf = (v, q = 1) => { if (!v || !bonus) return v; const c = Math.max(v.compare || 0, v.price); const u = Math.round(v.price / q); return Object.assign({}, v, { price: (u - Math.floor(u * bonus / 100)) * q, compare: c }); };
    const mvB = st.sel === 'single' ? bf(mv) : mv;
    setPrice('single', mvB, !!(mv && mv.available));
    let line = null; let label = S.main.label;
    if (st.sel === 'single') { line = mvB; label = S.main.label + (mv && S.main.options.length && mv.title !== 'Default Title' ? ' · ' + mv.title : ''); }
    S.groups.forEach((g) => {
      const r = this.groupVal(g, mv);
      setPrice(g.id, r.v, r.keys.some((k) => r.map[k].available));
      const parts = (r.val || '').split(' | ');
      this.querySelectorAll(`[data-cal-sv="${g.id}"]`).forEach((b) => {
        const on = b.dataset.part !== undefined ? parts[+b.dataset.part] === b.dataset.val : r.val === b.dataset.val;
        b.setAttribute('aria-checked', on ? 'true' : 'false');
        // verfügbar? (bei Kombi: mit dem anderen Teil kombiniert)
        let k = b.dataset.val;
        if (b.dataset.part !== undefined) { const p = parts.slice(); p[+b.dataset.part] = b.dataset.val; k = p.join(' | '); }
        const hit = r.map[k];
        b.disabled = !hit || !hit.available;
      });
      if (st.sel === g.id) { line = r.v; label = g.label + (r.val && r.keys.length > 1 ? ' · ' + r.val : ''); }
    });
    // Zusatz
    let extra = 0; let extraCompare = 0;
    S.addons.forEach((a) => {
      const box = this.querySelector(`[data-cal-addon="${a.id}"]`); if (!box) return;
      const vis = this.addonVisible(a); box.hidden = !vis;
      const a0 = this.st.addon[a.id]; const p = a.bonus && bonus ? bf(this.addonPick(a), a.qty || 1) : this.addonPick(a);
      const cb = box.querySelector('[data-cal-addon-on]'); if (cb) cb.checked = a0.on;
      box.classList.toggle('is-on', a0.on && vis);
      this.querySelectorAll(`[data-cal-aprice="${a.id}"]`).forEach((el) => { el.textContent = p ? '+ ' + money(p.price, fmt) : '–'; });
      this.querySelectorAll(`[data-cal-awas="${a.id}"]`).forEach((el) => { const show = p && p.compare > p.price; el.hidden = !show; if (show) el.textContent = money(p.compare, fmt); });
      this.querySelectorAll(`[data-cal-asave="${a.id}"]`).forEach((el) => { const show = p && p.compare > p.price; el.hidden = !show; if (show) el.textContent = el.dataset.pctOnly !== undefined ? 'Du sparst ' + Math.round((p.compare - p.price) / p.compare * 100) + ' %' : 'Du sparst ' + money(p.compare - p.price, fmt).replace(/,00(?=\D*$)/, '') + (el.dataset.pct !== undefined ? ' (' + Math.round((p.compare - p.price) / p.compare * 100) + ' %)' : ''); });
      box.querySelectorAll('[data-cal-av]').forEach((b) => {
        b.setAttribute('aria-checked', a0.vals[+b.dataset.oi] === b.dataset.val ? 'true' : 'false');
        const test = a0.vals.slice(); test[+b.dataset.oi] = b.dataset.val; const hit = a.map[test.join(' | ')];
        b.disabled = !hit || !hit.available;
      });
      if (vis && a0.on && p && p.available) { extra += p.price; extraCompare += Math.max(p.compare, p.price); }
    });
    const total = (line ? line.price : 0) + extra;
    const ok = !!(line && line.available);
    this.querySelectorAll('[data-cal-price]').forEach((el) => { el.textContent = ok ? money(total, fmt) : 'Nicht verfügbar'; });
    this.wasTotal(ok ? (line ? Math.max(line.compare || 0, line.price) : 0) + extraCompare : 0, ok ? total : 0, fmt);
    this.querySelectorAll('[data-cal-optlabel]').forEach((el) => { el.textContent = label.replace(/\s*\([^)]*\)/g, ''); });
    // Gratis-Beigaben: mit „2. Kissen dazu“ (Zusatz mit eigenen Beigaben) oben verdoppelt anzeigen
    const twice = S.addons.some((a) => this.addonVisible(a) && this.st.addon[a.id].on && (a.free || []).length);
    this.querySelectorAll('.cal-free__q').forEach((q) => { q.hidden = !twice; });
    this.querySelectorAll('.cal-free__p s[data-c1]').forEach((x) => { x.textContent = twice ? x.dataset.c2 : x.dataset.c1; });
    this.querySelectorAll('.cal-opt__badge[data-b2]').forEach((x) => { x.textContent = twice ? x.dataset.b2 : x.dataset.b1; });
    // Auswahl ausblenden, wenn alle Werte eines Felds automatisch auf einen bevorzugten Wert stehen
    if (this.prefHide && this.pref && this.pref.length) {
      this.querySelectorAll('.cal-optbox__panel, .cal-addon__panel').forEach((pn) => {
        const rows = [...pn.querySelectorAll('.cal-chips__row')];
        const auto = rows.length === 0 || rows.every((r) => { const c = r.querySelector('[aria-checked="true"]'); return c && !c.disabled && this.pref.includes(String(c.dataset.val).trim().toLowerCase()); });
        pn.classList.toggle('is-auto', auto);
      });
    }
    this.heights();
    this.querySelectorAll('[data-cal-add]').forEach((b) => { b.disabled = !ok; });
    const id = this.querySelector('[data-cal-form-id]'); if (id && line) id.value = line.id;
    // Bild der Produktvariante zeigen
    if (mv && mv.img && this._lastImg !== mv.img) {
      const img = this.querySelector('.cal-gal__img');
      if (this._setImg) { if (this._base) this._base.main = mv.img; } // Set-Bild bleibt vorne, das Produktbild steht in der Ecke
      else if (img && this._lastImg !== undefined) { img.srcset = ''; img.src = mv.img; if (this._base) this._base.main = mv.img; }
      const th = this.querySelector('.cal-subnav__thumb[data-follow]'); if (th && this._lastImg !== undefined) { th.srcset = ''; th.src = mv.img; }
      this._lastImg = mv.img;
    }
    // Set gewählt: Bild des Sets (Bundle-Produkt) als Hauptbild, Produktbild klein in der Ecke zum Zurückwechseln
    // Ist „Einzeln“ ausgeblendet, steht die Startoption für das Produkt der Seite (z. B. „SIDE+ inklusive Wechselbezug“) – dafür kein Set-Bild
    if (this._selInit === undefined) {
      const sb = this.querySelector('[data-cal-box-of="single"]');
      this._selInit = sb && sb.hidden ? this.st.sel : null;
    }
    const sg = this.st.sel !== 'single' && this.st.sel !== this._selInit ? S.groups.find((x) => x.id === this.st.sel) : null;
    this.galSet(sg && sg.img ? sg.img : null);
    this._giftSel = line ? line.id : null;
    this.ship(total);
  }

  setsLines() {
    const S = this.sets; const mv = this.mainVariant();
    let line = null;
    if (this.st.sel === 'single') line = mv;
    else { const g = S.groups.find((x) => x.id === this.st.sel); if (g) line = this.groupVal(g, mv).v; }
    if (!line || !line.available) return null;
    const lines = [{ id: line.id, quantity: 1 }];
    // Gratis-Beigaben als sichtbare Eigenschaft (Kasse + Bestellung), keine eigenen Positionen
    if (this.st.sel === 'single' && S.inkl) lines[0].properties = { Inklusive: S.inkl };
    // Gratis-Beigaben (Produkt-Metafeld custom.gratis_beigaben, 0 €): je Hauptprodukt bzw. je Zusatz einmal
    const free = {};
    // Gratis-Beigaben nur noch Anzeige (Lager: keine NICHT-PACKEN-Positionen im Auftrag)
    const addFree = () => {};
    if (this.st.sel === 'single') addFree(S.free, 1);
    S.addons.forEach((a) => {
      if (!this.addonVisible(a) || !this.st.addon[a.id].on) return;
      const p = this.addonPick(a); if (p && p.available) { lines.push(a.inkl ? { id: p.id, quantity: a.qty || 1, properties: { Inklusive: a.inkl } } : { id: p.id, quantity: a.qty || 1 }); addFree(a.free, a.qty || 1); }
    });
    Object.keys(free).forEach((id) => lines.push({ id: +id, quantity: free[id] }));
    // Aktion (z. B. Black Month) als sichtbare Eigenschaft je Position, damit die Ersparnis auch an der Kasse steht
    const bw = this.querySelector('[data-cal-sets-root]')?.dataset.bw;
    if (bw) {
      const tag = (v) => (v && v.compare > v.price ? bw + ' · du sparst ' + Math.round((v.compare - v.price) / v.compare * 100) + ' %' : bw);
      lines[0].properties = Object.assign({}, lines[0].properties, { Aktion: tag(line) });
      let k = 1;
      S.addons.forEach((a) => {
        if (!this.addonVisible(a) || !this.st.addon[a.id].on) return;
        const p = this.addonPick(a); if (!p || !p.available) return;
        if (lines[k]) lines[k].properties = Object.assign({}, lines[k].properties, { Aktion: tag(p) });
        k++;
      });
    }
    // Shopify zeigt die Positionen eines Requests umgekehrt an: rückwärts senden, damit das Hauptprodukt oben steht
    return lines.reverse();
  }

  current() {
    const o = this.data.options.find((x) => x.id === this.state.opt);
    const v = o.variants.find((x) => x.id === this.state.vars[o.id]) || o.variants[0];
    return { o, v };
  }

  render() {
    if (this.sets) return this.renderSets();
    const { o, v } = this.current();
    const fmt = this.dataset.money;
    this.querySelectorAll('[data-cal-opt]').forEach((b) => b.setAttribute('aria-checked', b.dataset.calOpt === o.id ? 'true' : 'false'));
    this.querySelectorAll('[data-cal-show-for]').forEach((x) => { x.hidden = x.dataset.calShowFor !== o.id; });
    this.querySelectorAll('[data-cal-chips-of]').forEach((x) => { x.hidden = x.dataset.calChipsOf !== o.id; });
    this.querySelectorAll('[data-cal-var]').forEach((b) => b.setAttribute('aria-checked', +b.dataset.calVar === this.state.vars[b.dataset.calOf] ? 'true' : 'false'));
    this.querySelectorAll('.cal-seg').forEach((s) => {
      const bs = [...s.querySelectorAll('.cal-seg__b')];
      s.style.setProperty('--seg-i', Math.max(0, bs.findIndex((b) => b.dataset.calOpt === o.id)));
    });
    // Preise je Option (nach Variante), Gesamtpreis, Texte
    this.data.options.forEach((op) => {
      const ov = op.variants.find((x) => x.id === this.state.vars[op.id]) || op.variants[0];
      this.querySelectorAll(`[data-cal-price-of="${op.id}"]`).forEach((el) => { el.textContent = money(ov.price, fmt); });
      if (ov.img) this.querySelectorAll(`[data-cal-opt="${op.id}"] .cal-varimg, [data-cal-show-for="${op.id}"] .cal-varimg`).forEach((im) => { im.srcset = ''; im.src = ov.img; });
    });
    this.querySelectorAll('[data-cal-price]').forEach((el) => { el.textContent = money(v.price, fmt); });
    this.wasTotal(v.compare || 0, v.price, fmt);
    this.querySelectorAll('[data-cal-optlabel]').forEach((el) => { el.textContent = o.variants.length > 1 ? `${o.label} · ${v.title}` : o.label; });
    const id = this.querySelector('[data-cal-form-id]'); if (id) id.value = v.id;
    this.ship(v.price);
  }

  wasTotal(cmp, price, fmt) {
    const show = cmp > price && price > 0;
    this.querySelectorAll('[data-cal-was-total]').forEach((el) => { el.hidden = !show; if (show) el.textContent = money(cmp, fmt); });
    this.querySelectorAll('[data-cal-pct-total]').forEach((el) => { el.hidden = !show; if (show) el.textContent = '−' + Math.round((cmp - price) / cmp * 100) + ' %'; });
  }
  gift(price) {
    if (price !== undefined) this._giftPrice = price;
    const p = this._giftPrice || 0; const fmt = this.dataset.money;
    this.querySelectorAll('[data-cal-gift]').forEach((g) => {
      const th = +g.dataset.th || 0; if (!th) return;
      // Liegt die gerade gewählte Variante schon im Warenkorb, zählt nur der Warenkorb (nicht doppelt); sonst Warenkorb + Auswahl
      const inCart = this._giftSel && (window.CalCartLines || []).some(([vid]) => vid === this._giftSel);
      const sum = (window.CalCartSub || 0) + (inCart ? 0 : p); const ok = sum >= th;
      g.classList.toggle('is-ok', ok);
      g.style.setProperty('--gift', Math.min(100, Math.round(sum / th * 100)) + '%');
      const t = g.querySelector('[data-cal-gift-t]');
      if (t) t.textContent = ok ? `${g.dataset.name} gratis dabei` : `Noch ${money(th - sum, fmt)} bis zum Gratis-${g.dataset.name}`;
    });
    // Versand + Geschenk als ein Balken mit zwei Stufen (snippets/cal-ship, Popups) – Summe wie im Warenkorb
    const free = +this.dataset.free || 0;
    this.querySelectorAll('[data-cal-steps]').forEach((s) => {
      const th = +s.dataset.th || 0; if (!th) return;
      const inCart = this._giftSel && (window.CalCartLines || []).some(([vid]) => vid === this._giftSel);
      const sum = (window.CalCartSub || 0) + (inCart ? 0 : p);
      const shipOk = free && sum >= free; const giftOk = sum >= th;
      s.classList.toggle('is-free', giftOk);
      s.style.setProperty('--ship', Math.min(100, Math.round(sum / th * 100)) + '%');
      s.querySelectorAll('[data-cal-m1]').forEach((m) => m.classList.toggle('is-on', !!shipOk));
      s.querySelectorAll('[data-cal-m2]').forEach((m) => m.classList.toggle('is-on', giftOk));
      const t = s.querySelector('[data-cal-ship-t]');
      if (t) t.textContent = giftOk ? `Versand + ${s.dataset.name} gratis` : shipOk ? `Versand gratis · noch ${money(th - sum, fmt)} bis zum Gratis-${s.dataset.name}` : `Noch ${money(free - sum, fmt)} bis zum kostenlosen Versand`;
    });
  }

  ship(price) {
    this.gift(price);
    const free = +this.dataset.free || 0;
    if (!free) return;
    const fmt = this.dataset.money;
    const pct = Math.min(100, Math.round(price / free * 100));
    const ok = price >= free;
    this.querySelectorAll('[data-cal-ship]:not([data-cal-steps])').forEach((s) => {
      s.classList.toggle('is-free', ok);
      s.style.setProperty('--ship', pct + '%');
      s.querySelector('[data-cal-ship-t]').textContent = ok ? 'Kostenloser Versand' : `Noch ${money(free - price, fmt)} bis zum kostenlosen Versand`;
      s.querySelector('[data-cal-ship-v]').textContent = ok ? 'inklusive' : `${money(price, fmt)} von ${money(free, fmt)}`;
    });
  }

  lage(t, opts = {}) {
    const dlg = t.closest('dialog');
    const tabs = [...dlg.querySelectorAll('[data-cal-lage]')];
    tabs.forEach((b) => b.setAttribute('aria-selected', b === t ? 'true' : 'false'));
    const tb = dlg.querySelector('.cal-tabs'); if (tb) { tb.style.setProperty('--tab-i', tabs.indexOf(t)); tb.style.setProperty('--i', tabs.indexOf(t)); tb.style.setProperty('--n', tabs.length); }
    // Bilder mit gleichem Ausschnitt (z. B. Kissenhöhen) gleiten ineinander über: altes Bild über dem neuen ausblenden
    const oldImg = dlg.querySelector('[data-cal-lage-panel]:not([hidden]) .cal-lage__ph img');
    dlg.querySelectorAll('[data-cal-lage-panel]').forEach((p) => { p.hidden = p.dataset.calLagePanel !== t.dataset.calLage; });
    const ph = dlg.querySelector('[data-cal-lage-panel]:not([hidden]) .cal-lage__ph');
    if (oldImg && ph && !opts.silent && (this.dataset.lageAnim || 'morph') === 'morph' && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
      ph.querySelectorAll('.cal-lage__ghost').forEach((g) => g.remove());
      const g = oldImg.cloneNode(); g.className = 'cal-lage__ghost'; g.removeAttribute('loading'); g.alt = '';
      ph.classList.add('is-morph'); ph.append(g);
      requestAnimationFrame(() => requestAnimationFrame(() => g.classList.add('is-out')));
      g.addEventListener('transitionend', () => { g.remove(); ph.classList.remove('is-morph'); }, { once: true });
    }
    dlg.querySelectorAll('[data-cal-lage-panel] video').forEach((v) => { const on = !v.closest('[data-cal-lage-panel]').hidden; if (on) { v.currentTime = 0; v.play?.().catch(() => {}); } else v.pause?.(); });
    // Vergleichs-Popup: zuletzt gewählte Größe/Wärmeklasse in beide Richtungen übernehmen
    const boxes = dlg.querySelectorAll('[data-cal-lage-panel]:not([hidden]) [data-cmpp]');
    boxes.forEach((b) => { delete b._cmpp; this.cmppInit(b); });
    if (!boxes.length && this._cmpKeys && this.sets && dlg.querySelector('[data-cmpp]')) this.cmppApplyMain();
    // Tab wählt eine Option der Kaufauswahl (z. B. Höhe „Hoch“)
    if (t.dataset.select && !opts.silent) {
      const want = t.dataset.select.toLowerCase();
      const btn = [...this.querySelectorAll('[data-cal-mo]')].find((b) => b.dataset.val.toLowerCase().startsWith(want));
      if (btn && btn.getAttribute('aria-checked') !== 'true') btn.click();
    }
  }

  open(name) {
    const d = this.querySelector(`[data-cal-dlg="${name}"]`);
    if (!d) return;
    // Set-Modus: dieselbe Auswahl ins Popup verschieben und beim Schließen zurück (ein Zustand, keine Kopie)
    if (this.sets && d.querySelector('[data-cal-sets-slot]')) {
      const root = this.querySelector('[data-cal-sets-root]'); const slot = d.querySelector('[data-cal-sets-slot]'); const home = this.querySelector('[data-cal-sets-home]');
      if (root && slot && home) { slot.append(root); d.addEventListener('close', () => home.append(root), { once: true }); }
    }
    d.querySelectorAll('[data-cmpp]').forEach((b) => { delete b._cmpp; });
    this._cmpKeys = null;
    // Kauf-Kästen ohne Tabs (z. B. Wechselbezug-Popup, snippets/cal-why) gleich vorbereiten
    d.querySelectorAll('[data-cmpp]').forEach((b) => { if (!b.closest('[data-cal-lage-panel]')) this.cmppInit(b); });
    d.querySelectorAll('.cal-lage__ph img[loading="lazy"]').forEach((i) => { i.loading = 'eager'; });
    const selTabs = [...d.querySelectorAll('[data-cal-lage][data-select]')];
    if (selTabs.length) {
      const cur = [...this.querySelectorAll('[data-cal-mo][aria-checked="true"]')].map((b) => b.dataset.val.toLowerCase());
      const hit = selTabs.find((tb) => cur.some((c) => c.startsWith(tb.dataset.select.toLowerCase())));
      if (hit) this.lage(hit, { silent: true });
    }
    if (typeof d.showModal === 'function') d.showModal(); else d.setAttribute('open', '');
    // Fokus auf das Popup selbst statt auf die erste Option – sonst zeigt Safari (iOS) dort einen zweiten Ring
    if (!d.hasAttribute('tabindex')) d.tabIndex = -1;
    try { d.focus({ preventScroll: true }); } catch (e) { d.focus(); }
  }

  watchAtc() {
    const bar = this.querySelector('[data-cal-mbar]');
    const atc = this.querySelector('.cal-atc');
    // Höhe der mobilen Leiste als Variable (mehrzeilige Variantentexte), damit der Schlafberater-Button darüber sitzt
    if (bar && 'ResizeObserver' in window) {
      new ResizeObserver(() => { if (bar.offsetHeight) document.documentElement.style.setProperty('--cal-mbar-h', bar.offsetHeight + 'px'); }).observe(bar);
    }
    if (!('IntersectionObserver' in window)) return;
    // Ausblenden, solange Kaufoptionen (Bundle-/Set-Wahl) oder der Warenkorb-Button im Bild sind
    if (bar && atc) {
      const vis = new Map();
      const io = new IntersectionObserver((es) => { es.forEach((x) => vis.set(x.target, x.isIntersecting)); bar.classList.toggle('is-hidden', [...vis.values()].some(Boolean)); });
      [atc, this.querySelector('[data-cal-sets-home]'), this.querySelector('.cal-buy__opts')].filter(Boolean).forEach((n) => io.observe(n));
    }
    // Am Seitenende (Footer sichtbar) ausblenden, damit Zahlungs-Icons und Links frei bleiben
    if (bar) {
      const seen = new Map();
      const io = new IntersectionObserver((es) => { es.forEach((x) => seen.set(x.target, x.isIntersecting)); bar.classList.toggle('is-end', [...seen.values()].some(Boolean)); });
      document.querySelectorAll('footer > .shopify-section').forEach((n) => { if (!n.querySelector('cal-finder') || n.children.length > 1) io.observe(n); });
    }
    // Desktop: Produktleiste bleibt über die ganze Seite oben, sobald ihre Ausgangsposition oben verlassen ist
    const sub = this.querySelector('[data-cal-subnav]');
    if (sub) {
      const ph = document.createElement('div'); ph.className = 'cal-subnav__ph'; sub.before(ph);
      new IntersectionObserver(([e]) => {
        const fix = !e.isIntersecting && e.boundingClientRect.top < 0;
        sub.classList.toggle('is-fixed', fix); ph.style.height = fix ? sub.offsetHeight + 'px' : '1px';
      }).observe(ph);
    }
  }

  eta() {
    const els = this.querySelectorAll('[data-cal-eta]');
    if (!els.length) return;
    const cutoff = +this.dataset.cutoff || 14, dMin = +this.dataset.daysMin || 1, dMax = +this.dataset.daysMax || 3;
    const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Berlin' }));
    const add = (n) => {
      const d = new Date(now); let k = 0;
      if (d.getHours() >= cutoff) d.setDate(d.getDate() + 1);
      while (k < n) { d.setDate(d.getDate() + 1); if (d.getDay() !== 0 && d.getDay() !== 6) k++; }
      return d;
    };
    const a = add(dMin), b = add(dMax);
    const wd = (d) => d.toLocaleDateString('de-DE', { weekday: 'short' });
    const txt = `Lieferung ${wd(a)}, ${a.getDate()}. – ${wd(b)}, ${b.getDate()}. ${b.toLocaleDateString('de-DE', { month: 'long' })}`;
    els.forEach((el) => { el.textContent = txt; });
  }

  /* Höhe je Größe (Blöcke „Lage/Höhe“, Feld „Großer Wert“ als „40x80: 165 mm | 80x80: 200 mm“) */
  heights() {
    const el = this.querySelector('[data-cal-heights]'); if (!el) return;
    if (!this._hts) { try { this._hts = JSON.parse(el.textContent).map((h) => ({ sel: h.sel, map: Object.fromEntries(h.cm.split('|').map((p) => p.split(':').map((x) => x.trim())).filter((x) => x.length > 1).map(([k, v]) => [k.toLowerCase().replace(/\s|cm/g, ''), v])) })); } catch (e) { this._hts = []; } }
    const norm = (v) => String(v || '').toLowerCase().replace(/\(.*?\)/g, '').replace(/\s|cm/g, '').replace('×', 'x');
    const cur = [...this.querySelectorAll('[data-cal-mo][aria-checked="true"]')].map((b) => b.dataset.val);
    const size = cur.map(norm).find((v) => /^\d+x\d+$/.test(v));
    // Popup: passenden Wert zeigen
    this.querySelectorAll('[data-cal-size]').forEach((sp) => { const group = [...sp.parentElement.querySelectorAll('[data-cal-size]')]; const hit = group.find((g) => g.dataset.calSize === size) || group[0]; sp.hidden = sp !== hit; });
    // mm unter den Höhen-Optionen
    this.querySelectorAll('[data-cal-mo]').forEach((b) => {
      const h = this._hts.find((x) => b.dataset.val.toLowerCase().startsWith(x.sel));
      let mm = b.querySelector('.cal-oc__mm');
      const val = h && size ? h.map[size] : '';
      if (!val) { mm?.remove(); return; }
      if (!mm) { mm = document.createElement('small'); mm.className = 'cal-oc__mm'; (b.querySelector('.cal-oc__t') || b).after(mm); }
      mm.textContent = val;
    });
    // Kaufbox-Label „Höhe: Mittel · 200 mm“
    this.querySelectorAll('[data-cal-vo]').forEach((w) => {
      const l = w.querySelector('[data-cal-vo-label]'); if (!l) return;
      const v = (l.textContent || '').split(' · ')[0];
      const h = this._hts.find((x) => v.toLowerCase().startsWith(x.sel));
      const mm = h && size ? h.map[size] : '';
      l.textContent = mm ? v + ' · ' + mm : v;
    });
  }

  /* ---------- Vergleichs-Popup: andere Produkte direkt kaufen (snippets/cal-cmp-popup) ---------- */
  cmppKey(v) {
    const s = String(v || '').toLowerCase().replace(/\(.*?\)/g, '').replace(/cm|\s|×/g, (m) => (m === '×' ? 'x' : ''));
    if (/^\d+x\d+$/.test(s)) return s;
    for (const k of ['sommer', 'ganzjahr', 'winter', 'kühl', 'wärm']) if (s.includes(k)) return k;
    return s;
  }
  cmppCurrent() {
    // aktuelle Auswahl auf dieser Seite (Set-Modus oder normale Variante)
    let vals = [];
    if (this._cmpKeys) return this._cmpKeys;
    if (this.sets) vals = this.st.mo.slice();
    else { try { const { v } = this.current(); vals = (v && (v.options || (v.title || '').split(' / '))) || []; } catch (e) { vals = []; } }
    return vals.map((x) => ({ raw: String(x).replace(/\s*\(.*\)\s*$/, ''), key: this.cmppKey(x) }));
  }
  cmppApplyMain() {
    // Auswahl aus einem anderen Produkt im Popup auf das aktuelle Produkt übertragen (passende Werte je Option)
    const keys = this._cmpKeys.map((k) => k.key); let changed = false;
    this.st.mo.forEach((cur, i) => {
      const vals = [...new Set([...this.querySelectorAll(`[data-cal-mo="${i}"]`)].map((b) => b.dataset.val))];
      const hit = vals.find((v) => keys.includes(this.cmppKey(v)));
      if (hit && hit !== cur) { this.st.mo[i] = hit; changed = true; }
    });
    if (changed) this.render();
  }
  cmppInit(box) {
    if (box._cmpp) return this.cmppRender(box);
    const vars = JSON.parse(box.querySelector('[data-cmpp-v]').textContent);
    const n = vars[0] ? vars[0].o.length : 0;
    const cur = this.cmppCurrent();
    const sel = []; const notes = [];
    for (let i = 0; i < n; i++) {
      const vals = [...new Set(vars.map((v) => v.o[i]))];
      let pick = vals.find((val) => cur.some((c) => c.key === this.cmppKey(val)));
      if (!pick) {
        // gleiche Art (Jahreszeit/Maß) gewählt, aber hier nicht vorhanden → Hinweis
        const seasons = ['sommer', 'ganzjahr', 'winter'];
        const want = cur.find((c) => seasons.includes(c.key) && vals.some((val) => seasons.includes(this.cmppKey(val))));
        pick = vals.find((val) => vars.some((v) => v.o[i] === val && v.a)) || vals[0];
        if (want) notes.push(box.dataset.cmppHint.replace('[wert]', want.raw).replace('[ersatz]', pick.replace(/\s*\(.*\)\s*$/, '')));
      }
      sel.push(pick);
    }
    box._cmpp = { vars, sel, notes };
    this.cmppRender(box);
  }
  cmppRender(box) {
    const st = box._cmpp; const fmt = this.dataset.money;
    const v = st.vars.find((x) => x.o.every((y, i) => y === st.sel[i]));
    box.querySelectorAll('[data-cmpp-o]').forEach((b) => {
      const i = +b.dataset.cmppO; const val = b.dataset.val;
      b.setAttribute('aria-checked', st.sel[i] === val ? 'true' : 'false');
      const test = st.sel.slice(); test[i] = val;
      const hit = st.vars.find((x) => x.o.every((y, k) => y === test[k]));
      b.classList.toggle('is-out', !hit || !hit.a);
    });
    box.querySelectorAll('[data-cmpp-l]').forEach((l) => { l.textContent = (st.sel[+l.dataset.cmppL] || '').replace(/\s*\(.*\)\s*$/, ''); });
    const note = box.querySelector('[data-cmpp-note]'); if (note) { note.textContent = st.notes.join(' '); note.hidden = !st.notes.length; }
    const ok = !!(v && v.a);
    const pr = box.querySelector('[data-cmpp-price]'); if (pr && v) pr.innerHTML = money(v.p, fmt) + (v.c > v.p ? ' <s>' + money(v.c, fmt) + '</s>' : '');
    const sk = box.querySelector('[data-cmpp-stock]'); if (sk) { sk.textContent = !v ? 'Diese Kombination gibt es nicht' : ok ? 'Auf Lager' : 'In dieser Kombination ausverkauft'; sk.classList.toggle('is-out', !ok); }
    const add = box.querySelector('[data-cmpp-add]');
    if (add) { add.disabled = !ok; add.textContent = ok ? add.dataset.label + ' · ' + money(v.p, fmt) : 'Nicht verfügbar'; }
  }
  cmppClick(e) {
    const o = e.target.closest('[data-cmpp-o]');
    if (o) { const box = o.closest('[data-cmpp]'); if (!box._cmpp) this.cmppInit(box); box._cmpp.sel[+o.dataset.cmppO] = o.dataset.val; box._cmpp.notes = []; this._cmpKeys = box._cmpp.sel.map((x) => ({ raw: String(x).replace(/\s*\(.*\)\s*$/, ''), key: this.cmppKey(x) })); this.cmppRender(box); return true; }
    const a = e.target.closest('[data-cmpp-add]');
    if (a) { const box = a.closest('[data-cmpp]'); const st = box._cmpp; const v = st && st.vars.find((x) => x.o.every((y, i) => y === st.sel[i])); if (v && v.a) this.add(a, [{ id: v.id, quantity: 1 }]); return true; }
    return false;
  }

  async add(btn, forced) {
    const err = this.querySelector('[data-cal-err]');
    if (err) err.hidden = true;
    let lines;
    if (forced) lines = forced;
    else if (this.sets) { lines = this.setsLines(); if (!lines) return; } else { const { v } = this.current(); lines = [{ id: v.id, quantity: 1 }]; }
    // Herkunft je Position (versteckt: „_“ wird in Warenkorb und Kasse nicht gezeigt, steht aber in der Bestellung)
    const quelle = forced ? ((btn && btn.closest('[data-cal-quelle]')?.dataset.calQuelle) || 'vergleich-popup') : (btn && btn.closest('dialog') ? 'kaufbox-popup' : 'kaufbox');
    lines = lines.map((l) => Object.assign({}, l, { properties: Object.assign({}, l.properties, { _quelle: quelle }) }));
    btn?.classList.add('is-loading');
    const sectionIds = [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);
    const body = { items: lines };
    if (sectionIds.length) body.sections = sectionIds.join(',');
    let Ev = null, deferred = null;
    try {
      ({ CartLinesUpdateEvent: Ev } = await import('@shopify/events'));
      deferred = Ev.createPromise();
      (btn || this).dispatchEvent(new Ev({ action: 'add', context: 'product', lines: lines.map((l) => ({ merchandiseId: l.id, quantity: l.quantity })), promise: deferred.promise }));
    } catch (e) { /* Event optional */ }
    try {
      const routes = (window.Theme && window.Theme.routes) || {};
      window.__calAdding = true; // Geschenk-Prüfung (cal-cart.js) wartet und bekommt den Warenkorb unten gereicht
      const res = await fetch(routes.cart_add_url || '/cart/add.js', { method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify(body) });
      const data = await res.json();
      if (data.status) throw new Error(data.description || data.message || 'Konnte nicht in den Warenkorb gelegt werden.');
      this.trk('kaufbox_in_warenkorb', { quelle, auswahl: forced ? quelle : this.selName(), positionen: lines.length });
      try { if (typeof window.clarity === 'function') window.clarity('set', 'kaufbox_auswahl', forced ? 'vergleich-popup' : this.selName()); } catch (e) {}
      let cartNow = null;
      if (deferred && Ev) {
        const cart = await (await fetch((routes.cart_url || '/cart') + '.js')).json();
        cartNow = cart;
        window.__calAdding = false; if (window.calGiftCheck) setTimeout(() => window.calGiftCheck(cart), 0);
        deferred.resolve({ cart: Ev.createCartFromAjaxResponse(cart), detail: { items: cart.items, source: 'cal-buy', sections: data.sections, didError: false } });
      }
      this.querySelectorAll('dialog[open]').forEach((d) => d.close());
      const openCart = () => {
        const drawer = document.getElementById('cart-drawer');
        if (drawer && typeof drawer.open === 'function') drawer.open(); else if (!drawer) window.location.href = (routes.cart_url || '/cart');
      };
      // Popup nach „In den Warenkorb“ (assets/cal-xu.js, z. B. Wechselbezug zum SIDE+) – danach Warenkorb
      let upsell = false;
      if (!forced && window.CalXUAfterAdd) {
        // Auslöser = tatsächlich hinzugefügtes Produkt (bei Set-Auswahl das Set), damit z. B. ein Set, das den Bezug schon enthält, keinen weiteren angeboten bekommt
        let h = (location.pathname.split('/products/')[1] || '').split('/')[0];
        if (this.sets && this.st && this.st.sel !== 'single') { const g = this.sets.groups.find((x) => x.id === this.st.sel); if (g && g.handle) h = g.handle; }
        const name = (this.querySelector('.cal-subnav__t') || {}).firstChild?.textContent?.trim() || '';
        try { upsell = await window.CalXUAfterAdd(h, cartNow ? cartNow.items.map((i) => i.handle) : null, openCart, name); } catch (er) { upsell = false; }
      }
      if (!upsell) openCart();
    } catch (e) {
      if (deferred) deferred.reject?.(e);
      if (err) { err.textContent = e.message; err.hidden = false; }
    } finally {
      btn?.classList.remove('is-loading');
      if (window.__calAdding) { window.__calAdding = false; if (window.calGiftCheck) window.calGiftCheck(); }
    }
  }
}
// Skript lädt async (sections/cal-pdp-hero.liquid): nicht auf Shopify-/Drittanbieter-Skripte warten, aber erst nach dem
// vollständigen HTML starten (readyState „interactive“ kommt vor allen defer-Skripten und vor DOMContentLoaded)
const calBuyDef = () => { if (!customElements.get('cal-buy')) customElements.define('cal-buy', CalBuy); };
if (document.readyState === 'loading') document.addEventListener('readystatechange', calBuyDef, { once: true }); else calBuyDef();

// Warenkorb-Summe (ohne Geschenk) kommt aus assets/cal-cart.js → Geschenk-Fortschritt in allen Kaufboxen neu rechnen
document.addEventListener('cal:cart-sub', () => { document.querySelectorAll('[data-cal-gift]').forEach((g) => { const box = g.closest('[data-money]'); if (box && typeof box.gift === 'function') box.gift(); }); });
